import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MarkdownStreamingContext } from './CodeBlock';
import {
  flashcardsSpecToMarkdown,
  isInchatQuizEnabled,
  parseFlashcardsSpec,
} from '../../utils/chatQuizSpec';
import { trackChatUxEvent } from '../../utils/eventTracker';
import QuizCopyButton from './QuizCopyButton';
import QuizPlainView from './QuizPlainView';
import './InchatQuiz.css';

const isChatPage = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/chat');

const shuffledOrder = (length) => {
  const order = Array.from({ length }, (_, i) => i);
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
};

const ShuffleIcon = () => (
  <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 3 21 3 21 8" />
    <line x1="4" y1="20" x2="21" y2="3" />
    <polyline points="21 16 21 21 16 21" />
    <line x1="15" y1="15" x2="21" y2="21" />
    <line x1="4" y1="4" x2="9" y2="9" />
  </svg>
);

/** ```wq-flashcards 대화 속 플래시카드 — 눌러서 뒤집고, 이전·다음, 섞기. 키보드: Space/Enter 뒤집기, ←/→ 넘기기. */
const InchatFlashcards = ({ code }) => {
  const streaming = useContext(MarkdownStreamingContext);
  const parsed = useMemo(() => parseFlashcardsSpec(code), [code]);
  const enabled = isInchatQuizEnabled();
  const total = parsed.spec.cards.length;
  const [order, setOrder] = useState(() => Array.from({ length: total }, (_, i) => i));
  const [position, setPosition] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [shuffled, setShuffled] = useState(false);
  const shownRef = useRef(false);
  const flipTrackedRef = useRef(false);
  const copyMarkdown = useMemo(() => flashcardsSpecToMarkdown(parsed.spec), [parsed.spec]);

  useEffect(() => {
    setOrder((prev) => (prev.length === total ? prev : Array.from({ length: total }, (_, i) => i)));
    setPosition((prev) => Math.min(prev, Math.max(total - 1, 0)));
  }, [total]);

  useEffect(() => {
    if (streaming || shownRef.current || !isChatPage()) return;
    shownRef.current = true;
    if (parsed.ok) {
      trackChatUxEvent('inchat_quiz_shown', {
        sourceCount: total,
        stage: enabled ? 'flashcards' : 'flashcards_plain',
        errorCode: parsed.dropped ? 'partial' : 'none',
      });
    } else {
      trackChatUxEvent('inchat_quiz_invalid', { stage: 'flashcards', errorCode: parsed.error });
    }
  }, [enabled, parsed, streaming, total]);

  if (streaming || !enabled || !parsed.ok) {
    return (
      <QuizPlainView
        copyMarkdown={!streaming && total ? copyMarkdown : ''}
        kind="flashcards"
        markdown={total ? copyMarkdown : ''}
        pending={streaming}
      />
    );
  }

  const card = parsed.spec.cards[order[position] ?? 0];
  const go = (delta) => {
    setPosition((prev) => Math.min(Math.max(prev + delta, 0), total - 1));
    setFlipped(false);
  };
  const flip = () => {
    setFlipped((prev) => !prev);
    if (!flipTrackedRef.current && isChatPage()) {
      flipTrackedRef.current = true;
      trackChatUxEvent('inchat_flashcards_flip', { sourceCount: total });
    }
  };
  const toggleShuffle = () => {
    const next = !shuffled;
    setShuffled(next);
    setOrder(next ? shuffledOrder(total) : Array.from({ length: total }, (_, i) => i));
    setPosition(0);
    setFlipped(false);
  };
  const handleKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(-1);
    }
  };

  return (
    <section aria-label={parsed.spec.title} className="wq-quiz wq-cards-deck" onKeyDown={handleKeyDown}>
      <div className="wq-quiz__head">
        <div className="wq-quiz__meta">
          <span className="wq-quiz__title">{parsed.spec.title}</span>
          <span className="wq-quiz__count">{total}장 · 눌러서 뒤집어요</span>
        </div>
        <QuizCopyButton kind="flashcards" label={parsed.spec.title} markdown={copyMarkdown} />
      </div>
      <div className="wq-quiz__body">
        <button
          aria-label={flipped ? `뒷면: ${card.back}. 눌러서 앞면 보기` : `앞면: ${card.front}. 눌러서 뒷면 보기`}
          className={`wq-flashcard ${flipped ? 'is-flipped' : ''}`}
          onClick={flip}
          type="button"
        >
          <span className="wq-flashcard__inner">
            <span aria-hidden={flipped} className="wq-flashcard__face wq-flashcard__face--front">
              <span className="wq-flashcard__side">앞면</span>
              <span className="wq-flashcard__text wq-flashcard__text--front">{card.front}</span>
            </span>
            <span aria-hidden={!flipped} className="wq-flashcard__face wq-flashcard__face--back">
              <span className="wq-flashcard__side">뒷면</span>
              <span className="wq-flashcard__text">{card.back}</span>
            </span>
          </span>
        </button>
        <span aria-live="polite" className="wq-quiz__sr">{flipped ? `뒷면: ${card.back}` : ''}</span>
        <div className="wq-cards-deck__nav">
          <button
            aria-label="이전 카드"
            className="wq-cards-deck__nav-button"
            disabled={position === 0}
            onClick={() => go(-1)}
            type="button"
          >
            이전
          </button>
          <span aria-live="polite" className="wq-cards-deck__position">{position + 1} / {total}</span>
          <button
            aria-label="다음 카드"
            className="wq-cards-deck__nav-button"
            disabled={position === total - 1}
            onClick={() => go(1)}
            type="button"
          >
            다음
          </button>
        </div>
        <button
          aria-pressed={shuffled}
          className={`wq-cards-deck__shuffle ${shuffled ? 'is-on' : ''}`}
          onClick={toggleShuffle}
          type="button"
        >
          <ShuffleIcon />
          <span>{shuffled ? '섞었어요 · 원래 순서로' : '카드 섞기'}</span>
        </button>
      </div>
    </section>
  );
};

export default InchatFlashcards;
